import { autoUpdate, FloatingPortal, flip, shift, useFloating, type VirtualElement } from '@floating-ui/react';
import { type ReactNode, useEffect, useMemo } from 'react';
import { CustomThemeProvider } from 'sonata-react-components';

type MapPopoverProps = {
  boundary?: Element | null;
  open: boolean;
  render: () => ReactNode;
  x: number;
  y: number;
};

const BOUNDARY_PADDING = 8;

export function MapPopover({ boundary, open, render, x, y }: MapPopoverProps) {
  const virtualElement = useMemo<VirtualElement>(
    () => ({
      getBoundingClientRect: () =>
        ({
          bottom: y,
          height: 0,
          left: x,
          right: x,
          top: y,
          width: 0,
          x,
          y
        }) as DOMRect
    }),
    [x, y]
  );

  const detectOverflowOptions = { boundary: boundary ?? 'clippingAncestors', padding: BOUNDARY_PADDING } as const;

  // `bottom-start` keeps the popover's top-left corner at (x, y), which is what every caller expects;
  // flip/shift only move it when it would overflow the map container.
  const { floatingStyles, refs } = useFloating<HTMLDivElement>({
    middleware: [flip(detectOverflowOptions), shift({ ...detectOverflowOptions, crossAxis: true })],
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate
  });

  // Virtual references must go through setPositionReference: `elements.reference` is only read on mount,
  // so the popover would stay anchored to the initial (0, 0) point.
  useEffect(() => {
    refs.setPositionReference(virtualElement);
  }, [refs, virtualElement]);

  if (!open) {
    return null;
  }

  return (
    <FloatingPortal>
      <CustomThemeProvider themeContrast="light">
        <div
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            zIndex: 10000
          }}
        >
          {render()}
        </div>
      </CustomThemeProvider>
    </FloatingPortal>
  );
}
